import React, { FC, ReactNode, useCallback, useContext, useMemo } from 'react';
import type { IOptionBase } from '../models';
import { classnames } from '../../../utils/classnames';
import { getStorageUrl } from '../../../utils/storageUrl';
import { Divider } from '../../Divider/Divider';
import { ActionMeta, OnChangeValue } from 'react-select';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { Box } from '@src/atoms/Box';
import { Link } from '@src/atoms/Link';
import { Text } from '@src/atoms/Text';
import { Icon } from '@src/atoms/Icon';
import { AnyType, TSize, TStorage } from '@src/models/common';
import { Tooltip } from '@src/atoms/Tooltip';

import styled from './SelectOption.scss';

interface IProps {
  data: IOptionBase;
  storage?: TStorage;
  isFocused?: boolean;
  values?: IOptionBase[];
  content?: ReactNode;
  classNamePrefix?: string | null;
  onChange: (newValue: OnChangeValue<AnyType, AnyType>, actionMeta: ActionMeta<AnyType>) => void;
  getOptionLabel?: (v: AnyType) => AnyType;
  getOptionValue?: (v: AnyType) => AnyType;
  isSearchView?: boolean;
  selectId: string;
  setMenuMaxWidth?: number;
  imageSize?: TSize;
}

/** Общий компонент для вывода опции */
export const SelectOption: FC<IProps> = (props) => {
  const {
    data,
    isFocused,
    onChange,
    classNamePrefix,
    content,
    getOptionLabel,
    getOptionValue,
    storage,
    values,
    isSearchView,
    selectId,
    setMenuMaxWidth,
    imageSize = 'sm',
  } = props;
  const { isMobile } = useContext(BreakpointsContext);

  const currentLabel = getOptionLabel?.(data) || data.label;

  const getCurrentOptionValue = useCallback(
    (val: AnyType) => {
      if (getOptionValue) {
        return getOptionValue(val);
      }

      return val.value;
    },
    [getOptionValue],
  );

  const isActiveValue = useMemo(
    () =>
      values?.some((v) => {
        return getCurrentOptionValue(v) === getCurrentOptionValue(data);
      }),
    [data, getCurrentOptionValue, values],
  );

  const handleClick = useCallback(() => {
    const action: ActionMeta<AnyType> = isActiveValue
      ? { action: 'deselect-option', option: data }
      : {
          action: 'select-option',
          option: data,
        };

    isSearchView && onChange(data, action);
  }, [data, isActiveValue, onChange, isSearchView]);

  const classes = classnames(
    styled.root,
    classNamePrefix,
    !isMobile && {
      [styled.rootFocused]: isFocused || isActiveValue,
    },
  );

  const textClasses = classnames(styled.contentLabel, !!setMenuMaxWidth && styled.maxWidthOption);

  const src = storage ? getStorageUrl(storage, data.img) : data.img;
  const id = `${selectId}_option_${data.value}`;
  const textPadding = isActiveValue ? 8 : 24;
  const imageClass = classnames(styled.rootImg, styled[`rootImg_${imageSize}`]);

  return (
    <Box column data-id={data.href ? undefined : id} data-elem="select-option" data-testid={id}>
      <Link.Wrapper id={id} href={data.href} disableNextJs={data.disableNextJs}>
        <Box
          paddingLeft={16}
          paddingRight={16}
          gap={8}
          alignItems="center"
          className={classes}
          onClick={handleClick}
          fullWidth
        >
          {data.renderOption ? (
            data.renderOption({ props })
          ) : (
            <>
              {data.dotColor && <Box className={styled.dot} bgColor={data.dotColor} />}
              {data.img && (
                <img
                  src={src}
                  alt={typeof currentLabel === 'string' ? currentLabel : src || 'defaultAlt'}
                  className={imageClass}
                  data-id={`${id}_image`}
                />
              )}
              <Box className={styled.content} column fullWidth>
                <Box justifyContent="spaceBetween" fullWidth style={{ height: '100%' }} alignItems="center">
                  <Text
                    id={'contentMobileLabel'}
                    className={textClasses}
                    noOfLines={!!setMenuMaxWidth ? 1 : undefined}
                    style={!!setMenuMaxWidth ? { paddingRight: textPadding } : undefined}
                  >
                    {content || currentLabel}
                  </Text>
                  {isActiveValue && <Icon name="check" view={isMobile ? 'basicBlack' : 'labelOpenCabinet'} size="sm" />}

                  {data.tooltip && (
                    <Box marginLeft={8} onClick={(e) => e.stopPropagation()}>
                      <Tooltip {...data.tooltip} trigger={isMobile ? 'click' : 'hover'}>
                        <Icon name={'circleOutlineQuestion'} size={isMobile ? 20 : 16} />
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
        {isMobile && <Divider />}
      </Link.Wrapper>
    </Box>
  );
};
